import { Component, OnInit, Input, Output, EventEmitter, ElementRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { StringHelper } from '../../../../shared/utils/string-helper';
import { ITimeSpan, ITimeBlock, IRoomScheduleInfo, IDisplayItem } from './room-allocation-table.component';
import { ScheduleItem } from '../room-schedule-item/room-schedule-item.component';
import { OccupancyType } from 'src/app/shared/models/occupancy';
import { ICreateReservationParams } from '../create-reservation-dialog/create-reservation-dialog-base.component';
import { ResourceType } from 'src/app/shared/models/resource-type';
import { TranslateService } from '@ngx-translate/core';
import { RoomService } from 'src/app/shared/services/rest/room.service';
import { ResourceTableRow } from 'src/app/shared/models/resource-table-row';

@Component({
  selector: 'app-room-allocation-desktop',
  templateUrl: './room-allocation-desktop.component.html',
  styleUrls: ['./room-allocation-desktop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoomAllocationDesktopComponent implements OnInit {

  @Input() model: AllocationTableModel;

  @Input() openResourceCalendarText: string;

  @Input() openResourceInfoText: string;

  /**
   * Date of the displayed day
   *
   * @type {Date}
   * @memberof RoomAllocationMobileComponent
   */
  @Input() baseDate: Date;

  @Output() openRoomInfo = new EventEmitter<number>();

  @Output() openSchedule = new EventEmitter<IRoomScheduleInfo>();

  /**
   * Event raised on click on "create new"
   *
   * @memberof RoomScheduleItemComponent
   */
  @Output() createReservation = new EventEmitter<ICreateReservationParams>();

  /**
   * Event raised when there is time conflict for the current data + time value constellation
   *
   * @memberof RoomScheduleItemComponent
   */
  @Output() timelineReservationConflicted = new EventEmitter<ITimeSpan>();

  @ViewChild('progressBar') progressBar: ElementRef;

  private displayTimeSpanStart = 5;

  private displayTimeSpan = 17;

  timeStream: number[];

  colspans: number[] = [1, 3, 3, 3, 3, 3, 1];

  constructor(
    private translate: TranslateService,
    protected room: RoomService
  ) { }

  ngOnInit() {
    this.timeStream = [6, 9, 12, 15, 18, 21];
  }

  get resourceType(): string {
    switch (this.model.resourceType) {
      case ResourceType.Raum:
        return 'ROOM';
      case ResourceType.Gerät:
        return 'DEVICE';
      default:
        throw Error(`Support for type '${this.model.resourceType}' not implemented`);
    }
  }

  /**
   * checks if the items of a ressource is vacant and open for booking
   * @param item items to check for a possible free time slot
   * @returns {boolean}
   */
  hasAnyFreeSlot(item: ScheduleItem[]) {
    return item.some(i => i.type === OccupancyType.Reservable);
  }

  warningClass(item: IDisplayItem): string {
    return item.isBookable ? 'text-warning' : 'text-danger';
  }

  /**
    * format time (eg. 15:00)
    *
    * @param {number} hour
    * @param {number} min
    * @returns
    * @memberof RoomAllocationComponent
    */
  getTimeString(hour: number, min: number): string {
    return StringHelper.formatTime(hour, min);
  }

  /**
    * format timespan (eg. 13:00 -  17:45)
    *
    * @param {ITimeSpan} span
    * @returns
    * @memberof RoomAllocationComponent
    */
  getTimeSpanString(span: ITimeSpan): string {
    const sep = '#';

    const timespan = StringHelper.formatTimeSpan(span.fromHour, span.fromMin, span.toHour, span.toMin, sep);
    const occupiedPhrase = this.translate.instant('OCCUPIED');
    const fromPhrase = this.translate.instant('TIME_FROM');
    const toPhrase = this.translate.instant('TIME_TO');

    return `${occupiedPhrase} ${fromPhrase.toLowerCase()} ${timespan.replace(sep, ` ${toPhrase.toLowerCase()} `)}`;
  }

  /**
   * transforms a list of truthy + falsy values to 1 or 0 string respectively
   * @param boolList a list of truthy + falsy values to transform
   * @returns {string[]}
   */
  toFlagSet(boolList) {
    return boolList.map(li => Boolean(li) === true ? '1' : '0').join('');
  }

  /**
   * create list of time blocks
   *
   * @returns {ITimeSpan[]}
   * @memberof RoomAllocationComponent
   */
  getTimeLine(scheduleItems: ScheduleItem[]): ITimeBlock[] {
    if (scheduleItems === null) {
      return [];
    }

    const maxHour = this.displayTimeSpanStart + this.displayTimeSpan;
    // init
    const blocks = new Array<ITimeBlock>(maxHour);
    for (let i = this.displayTimeSpanStart; i <= maxHour; i++) {
      blocks[i] = ({ hour: i, events: [] });
    }

    scheduleItems.filter(x => x.type !== OccupancyType.Reservable).forEach(x => {
      const alloc = this.getTimeSpan(
        x.from.getHours(),
        x.from.getMinutes(),
        x.to.getHours(),
        x.to.getMinutes(),
        true);

      // discard when end time is before timeline start time
      if (alloc.toHour < this.displayTimeSpanStart ||
        alloc.fromHour >= maxHour) {
          return;
      }

      // adjust offsets
      if (alloc.fromHour < this.displayTimeSpanStart) {
        alloc.fromHour = this.displayTimeSpanStart;
      }

      if (alloc.toHour > maxHour) {
        alloc.toHour = maxHour;
      }
      if (alloc.toHour === maxHour) {
        alloc.toMin = 0;
      }

      // add
      blocks[alloc.fromHour].events.push(alloc);
    });

    return blocks.slice(this.displayTimeSpanStart, maxHour);
  }

  /**
   * create a ITimeSpan object
   *
   * @private
   * @param {number} fromHour
   * @param {number} fromMin
   * @param {number} toHour
   * @param {number} toMin
   * @param {boolean} [inUse=false]
   * @returns {ITimeSpan}
   * @memberof RoomAllocationDesktopComponent
   */
  private getTimeSpan(fromHour: number, fromMin: number, toHour: number, toMin: number, inUse: boolean = false): ITimeSpan {
    return { fromHour: fromHour, fromMin: fromMin, toHour: toHour, toMin: toMin, inUse: inUse };
  }

  /**
   * Calculate the distance from left border of progrssbar-frame
   *
   * @param {ITimeSpan} span
   * @returns {string}
   * @memberof RoomAllocationDesktopComponent
   */
  showTimeStreamLeft(span: ITimeSpan): string {
    const timespanMinutes = span.fromHour * 60 + span.fromMin;
    const calcBase = timespanMinutes - (this.displayTimeSpanStart) * 60;
    return Math.round(calcBase / (this.displayTimeSpan * 60) * 100) + '%';
  }

  showTimeStreamWidth(span: ITimeSpan): string {
    const minutes = (span.toHour * 60 + span.toMin) - (span.fromHour * 60 + span.fromMin);
    return Math.round(minutes / (this.displayTimeSpan * 60) * 100) + '%';
  }

  zebra(index: number): string {
    return index % 2 === 0 ? 'bg-light' : '';
  }

  needSpace(index: number): boolean {
    return index < 21;
  }

  /**
   * handle click on "open roominfo" button
   *
   * @param {IDisplayItem} item
   * @memberof RoomAllocationDesktopComponent
   */
  onOpenRoomInfo(item: IDisplayItem, event: MouseEvent) {
    event.preventDefault();
    this.openRoomInfo.emit(item.resourceId);
  }

  /**
   * handle click on "open room schedule" button
   *
   * @param {IDisplayItem} item
   * @memberof RoomAllocationDesktopComponent
   */
  onOpenSchedule(item: IDisplayItem) {
    this.openSchedule.emit({
      roomId: item.resourceId,
      designation: item.designation,
      isBookable: item.isBookable,
      hasWarnings: Boolean(item.warning)
    });
  }

  onClickTimeline(event: MouseEvent, item: IDisplayItem) {
    // relative mous position click
    const width = this.progressBar.nativeElement.clientWidth;
    const left = event.offsetX;

    // calc time
    const decimalHour = (this.displayTimeSpan * (left / width)) + this.displayTimeSpanStart;
    let startHour = Math.floor(decimalHour);
    const startMinute = Math.round(60 * (decimalHour % 1));

    // round to 15 min
    let startQuarterHour = Math.round(startMinute / 15) * 15;
    if (startQuarterHour === 60) {
      startQuarterHour = 0;
      startHour++;
    }

    // create from date
    const date = this.baseDate;
    const from = new Date(date.getFullYear(), date.getMonth(), date.getDate(), startHour, startQuarterHour, 0, 0);

    // raise event
    this.createReservation.emit({
      idReservation: null,
      idRessource: item.resourceId,
      from: from,
      to: null
    });
  }

  onCreateReservationByButton(item: IDisplayItem) {

    const bd = this.baseDate;
    const BASE_MINUTES = 15;
    const nd = new Date();
    const fromDate = new Date(
      bd.getFullYear(),
      bd.getMonth(),
      bd.getDate(),
      nd.getHours(),
      Math.round((nd.getMinutes() + 5 ) / 5) * 5);

    const toDate = new Date(
      bd.getFullYear(),
      bd.getMonth(),
      bd.getDate(),
      nd.getHours(),
      Math.round((nd.getMinutes() + 5 + BASE_MINUTES) / 5) * 5);

    // figure out the best time free time slot to load forms with
    const nowTimestamp = fromDate.getTime();
    const nowTillEndtimeDelta = 60 * (BASE_MINUTES + 5) * 1000; // 20min time frame minimum
    const allReservables = item.scheduleItems.filter(si => si.type === OccupancyType.Reservable);
    const allEndingInFuture = allReservables
      .filter(si => si.to.getTime() > nowTimestamp)
      .sort((li, ri) => li.to.getTime() - ri.to.getTime());
    const bestFuture = allEndingInFuture.find(i => i.to.getTime() - nowTimestamp >= nowTillEndtimeDelta) ||
      allEndingInFuture.slice(-1)[0] ||
      allReservables.slice(-1)[0];

    // raise create reservation event
    this.createReservation.emit({
      idReservation: null,
      idRessource: item.resourceId,
      from: nowTimestamp < bestFuture.from.getTime() ? bestFuture.from : new Date(nowTimestamp),
      to: toDate > bestFuture.to ? bestFuture.to : toDate
    });
  }

  /**
   * Last additional column in header must have colspan="2" (space for calendar button)
   *
   * @param {number} index
   * @returns {boolean}
   * @memberof RoomAllocationDesktopComponent
   */
  isLastHeaderCell(index: number): boolean {
    return index + 1 === this.model.additionalColums.length;
  }

  notifyOccupiedTimespan(occupancySlot: ITimeSpan) {
    this.timelineReservationConflicted.emit(occupancySlot);
  }
}

export class AllocationTableModel {
  public mainColumnTitle: string;
  public additionalColums: ResourceTableRow[];
  public calendarButtonText: string;
  public displayItems: IDisplayItem[];
  public resourceType: ResourceType;
}

/**
 * Row model for AllocationTableModel
 *
 * @export
 * @class AllocationTableItem
 */
export class AllocationTableItem {
  public id: string;
  public designation: string;
  public additionalColumsData: ResourceTableRow[];
  public warning: string;
}
